import ToggleCaret from '@orn/shared-web/components/ToggleCaret';
import * as React from 'react';

export interface ICollapsibleSettingsCardProps {
  /** Header content rendered to the left of the expand/collapse chevron (e.g. a name + relationship badge). */
  header: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  /** Accessible label for the toggle button (e.g. the dependent's name). */
  toggleLabel: string;
  /** Optional action rendered just left of the chevron (e.g. a delete button). */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Settings card chrome with a collapsible header. The header is a toggle button
 * that expands/collapses the padded body (the chevron beside it is a decorative
 * state indicator); the body unmounts when collapsed so it occupies no space. An
 * optional `headerAction` (e.g. a delete button) renders just left of the
 * chevron. Used for the per-dependent and per-vehicle cards in the settings forms.
 */
const CollapsibleSettingsCard = ({
  header,
  isExpanded,
  onToggle,
  toggleLabel,
  headerAction,
  children,
}: ICollapsibleSettingsCardProps) => (
  // overflow-anchor:none opts the card's subtree out of scroll anchoring: these
  // cards grow/shrink in place (expand/collapse, mode flips), and the browser
  // silently re-anchoring the pane scroll yanks it up — worst on tall viewports.
  <div className="bg-primary border-secondary shadow-card overflow-hidden rounded-2xl border [overflow-anchor:none]">
    <div className="relative">
      {/* The button is the entire header row — padding and caret included — so any
          click in it toggles. The action overlays a reserved spacer as a sibling
          (never nested in the button), keeping it independently clickable. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={toggleLabel}
        className="flex w-full cursor-pointer items-center gap-3 px-5 py-4 text-left"
      >
        <div className="min-w-0 flex-1">{header}</div>
        {headerAction != null && <span className="w-9 shrink-0" aria-hidden />}
        <span className="flex shrink-0 items-center" aria-hidden>
          <ToggleCaret isOpen={isExpanded} className="text-quaternary" />
        </span>
      </button>
      {headerAction != null && (
        <span className="absolute inset-y-0 right-[52px] flex items-center">{headerAction}</span>
      )}
    </div>
    {isExpanded && <div className="border-secondary flex flex-col gap-8 border-t px-5 py-6">{children}</div>}
  </div>
);

CollapsibleSettingsCard.displayName = 'CollapsibleSettingsCard';

export default CollapsibleSettingsCard;
